import React, { useState } from "react";
import { useSimulator } from "@/context/SimulatorContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { fmtEUR, fmtNum, fmtPct } from "@/lib/format";
import {
  Leaf,
  CircleDot,
  Cannabis,
  ShieldCheck,
  AlertTriangle,
  CircleAlert,
  CheckCircle2,
  Trophy,
  Ruler,
  Beaker,
  Info,
} from "lucide-react";

const LOT_META = {
  lot3: { icon: Leaf, color: "#ec4899", tagline: "Bioactive Botanicals" },
  lot4: { icon: CircleDot, color: "#f59e0b", tagline: "Fungi High Value" },
  lot5: { icon: Cannabis, color: "#84cc16", tagline: "Vertical Hops" },
};

const CLS_STYLE = {
  VALIDATED: "bg-emerald-500/15 text-emerald-300 border-emerald-500/40",
  LITERATURE: "bg-sky-500/15 text-sky-300 border-sky-500/40",
  ASSUMPTION: "bg-amber-500/15 text-amber-300 border-amber-500/40",
  USER_INPUT: "bg-slate-500/15 text-slate-300 border-slate-500/40",
  TO_VALIDATE: "bg-rose-500/15 text-rose-300 border-rose-500/40",
  CALCULATED: "bg-violet-500/15 text-violet-300 border-violet-500/40",
};

const Tag = ({ value }) => value ? (
  <Badge className={`text-[9px] font-semibold px-1.5 py-0 border ${CLS_STYLE[value] || CLS_STYLE.ASSUMPTION}`}>
    {value.replace("_", " ")}
  </Badge>
) : null;

const SCENARIOS = [
  { key: "conservative", label: "Conservative" },
  { key: "base", label: "Base" },
  { key: "optimistic", label: "Optimistic" },
];

export const NewLotsTab = () => {
  const { inputs, summary, audit, expansionRanking, updateNewLot, updateNewLotSpecies, updateNewLotPath } = useSimulator();
  const lots = inputs.new_lots || {};
  const nl = summary.newLots || { items: [], total: {}, count: 0 };
  const [showAudit, setShowAudit] = useState(false);

  const green = audit.filter((c) => c.level === "GREEN").length;
  const yellow = audit.filter((c) => c.level === "YELLOW").length;
  const red = audit.filter((c) => c.level === "RED").length;

  return (
    <div className="space-y-6">
      {/* HEADER */}
      <div className="flex items-start gap-4 flex-wrap">
        <div className="rounded-xl bg-fuchsia-500/10 border border-fuchsia-500/30 p-3">
          <Beaker className="h-7 w-7 text-fuchsia-400" />
        </div>
        <div className="flex-1 min-w-[300px]">
          <h2 className="font-display text-2xl font-bold text-slate-100">
            Lotti 3-4-5 — Bioactive · Fungi · Hops
          </h2>
          <p className="text-sm text-slate-400 mt-1 max-w-3xl">
            Estensione modello con 3 nuovi lotti B2B (nutraceutica · cosmetica · functional food).
            KPI principale: <span className="text-fuchsia-300 font-semibold">valore commerciale/m²/anno</span>.
            Ogni parametro è classificato secondo il sistema di governance.
            Nessuna doppia contabilizzazione tra fresco/essiccato/polvere/estratto.
          </p>
        </div>
        <Button
          data-testid="audit-model-button"
          onClick={() => setShowAudit((v) => !v)}
          className="bg-slate-800 hover:bg-slate-700 text-slate-100 h-9 border border-slate-700"
        >
          <ShieldCheck className="h-4 w-4 mr-2" />
          Audit Model
          <span className="ml-2 flex items-center gap-1 text-[10px]">
            <span className="text-emerald-400">{green}</span>·
            <span className="text-amber-400">{yellow}</span>·
            <span className="text-rose-400">{red}</span>
          </span>
        </Button>
      </div>

      {/* MODEL UPDATE SUMMARY */}
      <Card data-testid="model-update-summary" className="bg-gradient-to-br from-emerald-500/10 via-fuchsia-500/10 to-amber-500/10 border border-fuchsia-500/30 p-5">
        <div className="flex items-start gap-3 flex-wrap">
          <Trophy className="h-5 w-5 text-fuchsia-400 mt-1 shrink-0" />
          <div className="flex-1 min-w-[280px]">
            <div className="text-xs font-semibold uppercase tracking-widest text-fuchsia-300">
              Model Update Summary — Lotti 3-4-5
            </div>
            <div className="mt-3 grid grid-cols-1 md:grid-cols-4 gap-3 text-sm">
              <SummaryPill label="Superficie fisica agg." value={`${fmtNum(nl.total.physical_area_m2 || 0)} m²`} accent="text-slate-100" />
              <SummaryPill label="Ricavi incrementali" value={fmtEUR(nl.total.revenue || 0)} accent="text-emerald-300" />
              <SummaryPill label="OPEX incrementale" value={fmtEUR(nl.total.opex || 0)} accent="text-rose-300" />
              <SummaryPill label="EBITDA incrementale" value={fmtEUR(nl.total.ebitda || 0)} accent="text-sky-300" />
              <SummaryPill label="CAPEX totale" value={fmtEUR(nl.total.capex || 0)} accent="text-violet-300" />
              <SummaryPill label="EBITDA margin" value={fmtPct(nl.total.ebitda_margin || 0, 1)} accent="text-emerald-400" />
              <SummaryPill label="Energia incrementale" value={`${fmtNum((nl.total.energy_kwh_year || 0) / 1000, 1)} MWh`} accent="text-amber-300" />
              <SummaryPill label="Acqua incrementale" value={`${fmtNum(nl.total.water_m3_year || 0)} m³`} accent="text-sky-400" />
            </div>
          </div>
        </div>
      </Card>

      {/* AUDIT PANEL */}
      {showAudit && (
        <Card data-testid="audit-panel" className="bg-slate-900/70 border-slate-800 p-5 space-y-3">
          <h3 className="font-display text-lg font-semibold text-slate-100">
            Model Audit — Risultati verifica
          </h3>
          <div className="space-y-2 max-h-[350px] overflow-y-auto pr-1">
            {audit.map((c, i) => (
              <div
                key={i}
                data-testid={`audit-item-${i}`}
                className={`p-3 rounded-lg border ${
                  c.level === "RED" ? "border-rose-500/40 bg-rose-500/5" :
                  c.level === "YELLOW" ? "border-amber-500/40 bg-amber-500/5" :
                  "border-emerald-500/40 bg-emerald-500/5"
                }`}
              >
                <div className="flex items-start gap-2">
                  {c.level === "RED" && <CircleAlert className="h-4 w-4 text-rose-400 mt-0.5" />}
                  {c.level === "YELLOW" && <AlertTriangle className="h-4 w-4 text-amber-400 mt-0.5" />}
                  {c.level === "GREEN" && <CheckCircle2 className="h-4 w-4 text-emerald-400 mt-0.5" />}
                  <div className="flex-1 text-sm">
                    <div className="font-semibold text-slate-100">{c.category}</div>
                    <div className="text-slate-300 text-xs">{c.msg}</div>
                    {(c.current !== "-" || c.expected !== "-") && (
                      <div className="mt-1 text-[11px] font-mono-tab text-slate-500">
                        current: {c.current} · expected: {c.expected} · formula: {c.formula}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div className="pt-2 border-t border-slate-800 text-[11px] text-slate-500 italic">
            <Info className="h-3 w-3 inline mr-1" />
            NON correggere automaticamente i RED. Ogni scostamento va validato manualmente.
          </div>
        </Card>
      )}

      {/* LOTS */}
      <div className="grid grid-cols-1 gap-6">
        {["lot3", "lot4", "lot5"].map((key) => {
          const lot = lots[key];
          if (!lot) return null;
          const meta = LOT_META[key];
          const c = nl.items.find((x) => x.key === key)?.calc || {};
          const Icon = meta.icon;
          const scenarioKey = lot.scenario || "base";
          const hasWarnings = (c.warnings || []).length > 0;

          return (
            <Card
              key={key}
              data-testid={`new-lot-${key}`}
              className={`bg-slate-900/70 border p-6 space-y-4 ${
                lot.enabled ? "border-slate-700" : "border-slate-800 opacity-60"
              }`}
              style={lot.enabled ? { boxShadow: `0 0 20px ${meta.color}15` } : {}}
            >
              {/* Header */}
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="flex items-center gap-3 min-w-0 flex-1">
                  <div className="p-2 rounded-lg" style={{ background: `${meta.color}20`, border: `1px solid ${meta.color}40` }}>
                    <Icon className="h-5 w-5" style={{ color: meta.color }} />
                  </div>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <div className="font-display font-bold text-lg text-slate-100">{lot.name}</div>
                      <Tag value={lot.classification_species} />
                    </div>
                    <div className="text-xs text-slate-500 mt-0.5">{lot.description}</div>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-2">
                    <Label className="text-[10px] uppercase tracking-wider text-slate-500">Anno attivaz.</Label>
                    <Input
                      data-testid={`new-lot-${key}-activation-year`}
                      type="number"
                      value={lot.activation_year}
                      onChange={(ev) => updateNewLot(key, { activation_year: Number(ev.target.value) || 1 })}
                      min={1}
                      max={5}
                      className="h-8 w-16 bg-slate-950 border-slate-800 text-right font-mono-tab text-xs"
                    />
                  </div>
                  <Switch
                    data-testid={`new-lot-${key}-enabled`}
                    checked={lot.enabled}
                    onCheckedChange={(v) => updateNewLot(key, { enabled: v })}
                  />
                </div>
              </div>

              {/* Scenario selector */}
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-xs text-slate-500 uppercase tracking-wider font-semibold">Scenario:</span>
                {SCENARIOS.map((s) => (
                  <button
                    key={s.key}
                    data-testid={`new-lot-${key}-scenario-${s.key}`}
                    onClick={() => updateNewLot(key, { scenario: s.key })}
                    className={`px-3 py-1 rounded-md text-xs font-semibold border transition-all ${
                      scenarioKey === s.key
                        ? "text-slate-950 border-transparent"
                        : "text-slate-400 border-slate-700 hover:border-slate-600"
                    }`}
                    style={scenarioKey === s.key ? { background: meta.color } : {}}
                  >
                    {s.label}
                  </button>
                ))}
              </div>

              {/* Dimensional */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <MiniStat label="Superficie fisica" value={`${lot.physical_area_m2} m²`} tag={lot.classification_area} testId={`new-lot-${key}-physical`} />
                <MiniStat label="Livelli verticali" value={lot.vertical_levels} tag={lot.classification_area} />
                <MiniStat label="Superficie equivalente" value={`${lot.equivalent_area_m2} m²`} tag="CALCULATED" />
                {key === "lot5" && (
                  <MiniStat label="Piante totali" value={fmtNum(lot.total_plants)} tag="CALCULATED" testId="new-lot-lot5-plants" />
                )}
                {key !== "lot5" && (
                  <MiniStat label="Produzione BASE" value={`${fmtNum(lot.production_scenarios?.base || 0)} kg`} tag={lot.classification_production} testId={`new-lot-${key}-prod-base`} />
                )}
              </div>

              {/* Species distribution */}
              <div className="pt-3 border-t border-slate-800">
                <div className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-2">
                  Specie (allocazione %)
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  {lot.species.map((sp) => (
                    <div key={sp.key} data-testid={`new-lot-${key}-species-${sp.key}`} className="flex items-center gap-2 border border-slate-800 rounded-md p-2 bg-slate-950/40">
                      <div className="flex-1 min-w-0">
                        <div className="text-xs font-semibold text-slate-200 truncate">{sp.name}</div>
                        <div className="text-[10px] text-slate-500 truncate italic">{sp.actives}</div>
                      </div>
                      <Input
                        type="number"
                        value={sp.allocation_pct}
                        onChange={(ev) => updateNewLotSpecies(key, sp.key, { allocation_pct: Number(ev.target.value) || 0 })}
                        className="h-7 w-14 bg-slate-950 border-slate-800 text-right font-mono-tab text-xs"
                      />
                      <span className="text-[10px] text-slate-500">%</span>
                    </div>
                  ))}
                </div>
                <div className="mt-2 text-[11px] font-mono-tab text-slate-500">
                  Somma: <span className={Math.abs(c.species_sum - 100) < 0.5 ? "text-emerald-400" : "text-rose-400"}>{fmtNum(c.species_sum || 0, 0)}%</span> (deve essere 100%)
                </div>
              </div>

              {/* Commercialization path */}
              <div className="pt-3 border-t border-slate-800">
                <div className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-2 flex items-center gap-2">
                  Commercialization Path — NO double counting
                  <Tag value={lot.classification_path} />
                </div>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
                  {lot.commercialization_path.map((p) => (
                    <div key={p.key} data-testid={`new-lot-${key}-path-${p.key}`} className="border border-slate-800 rounded-md p-2 bg-slate-950/40">
                      <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 truncate">{p.name}</div>
                      <div className="flex items-center gap-1 mt-1">
                        <Input
                          type="number"
                          value={p.pct}
                          onChange={(ev) => updateNewLotPath(key, p.key, { pct: Number(ev.target.value) || 0 })}
                          className="h-7 w-full bg-slate-950 border-slate-800 text-right font-mono-tab text-xs"
                        />
                        <span className="text-[10px] text-slate-500">%</span>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="mt-2 text-[11px] font-mono-tab text-slate-500">
                  Somma: <span className={Math.abs(c.path_sum - 100) < 0.5 ? "text-emerald-400" : "text-rose-400"}>{fmtNum(c.path_sum || 0, 0)}%</span> (deve essere 100%)
                </div>
              </div>

              {/* Economics */}
              <div className="pt-3 border-t border-slate-800 grid grid-cols-2 md:grid-cols-4 gap-3">
                <KpiCell label="Ricavi" value={fmtEUR(c.revenue || 0)} tag={lot.classification_revenue} accent="text-emerald-300" testId={`new-lot-${key}-revenue`} />
                <KpiCell label="OPEX" value={fmtEUR(c.opex || 0)} tag={lot.classification_opex} accent="text-rose-300" />
                <KpiCell label="EBITDA" value={fmtEUR(c.ebitda || 0)} tag="CALCULATED" accent="text-sky-300" testId={`new-lot-${key}-ebitda`} />
                <KpiCell label="EBITDA margin" value={fmtPct(c.ebitda_margin || 0, 1)} tag="CALCULATED" accent="text-emerald-400" />
                <KpiCell label="CAPEX" value={fmtEUR(c.capex || 0)} tag={lot.classification_capex} accent="text-violet-300" />
                <KpiCell label="Payback (CF)" value={c.payback_cashflow ? `${c.payback_cashflow.toFixed(1)} anni` : "N/D"} tag="CALCULATED" accent="text-amber-300" />
                <KpiCell label="ROI operativo" value={fmtPct(c.roi_operating_annual || 0, 1)} tag="CALCULATED" accent="text-fuchsia-300" />
                <KpiCell label="Ricavo/m²/anno" value={fmtEUR(c.revenue_per_m2 || 0)} tag="CALCULATED" accent="text-slate-200" />
              </div>

              {/* Utility */}
              <div className="pt-3 border-t border-slate-800 grid grid-cols-2 gap-3">
                <KpiCell label="Energia annua" value={`${fmtNum((lot.energy_kwh_year || 0) / 1000, 1)} MWh`} tag={lot.classification_utilities} accent="text-amber-300" />
                <KpiCell label="Acqua annua" value={`${fmtNum(lot.water_m3_year || 0)} m³`} tag={lot.classification_utilities} accent="text-sky-400" />
              </div>

              {/* Sanity warnings */}
              {hasWarnings && (
                <div className="pt-3 border-t border-slate-800 space-y-1">
                  {(c.warnings || []).map((w, i) => (
                    <div
                      key={i}
                      data-testid={`new-lot-${key}-warning-${i}`}
                      className={`text-xs flex items-start gap-2 rounded-md p-2 ${
                        w.level === "CRITICAL" ? "bg-rose-500/10 border border-rose-500/30 text-rose-300" : "bg-amber-500/10 border border-amber-500/30 text-amber-300"
                      }`}
                    >
                      {w.level === "CRITICAL" ? <CircleAlert className="h-3.5 w-3.5 shrink-0 mt-0.5" /> : <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-0.5" />}
                      <span><b>{w.level}:</b> {w.msg}</span>
                    </div>
                  ))}
                </div>
              )}
            </Card>
          );
        })}
      </div>

      {/* EXPANSION OPTIMIZER */}
      <Card data-testid="expansion-optimizer" className="bg-slate-900/70 border-slate-800 p-6">
        <div className="flex items-center gap-3 mb-4">
          <Trophy className="h-5 w-5 text-amber-400" />
          <h3 className="font-display text-lg font-semibold text-slate-100">Expansion Optimizer — Ranking</h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {expansionRanking.map((item, idx) => {
            const meta = LOT_META[item.key];
            const Icon = meta.icon;
            return (
              <div
                key={item.key}
                data-testid={`expansion-rank-${idx + 1}`}
                className={`border rounded-lg p-4 ${idx === 0 ? "border-emerald-500/40 bg-emerald-500/5" : "border-slate-800 bg-slate-950/40"}`}
              >
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <span className="font-display font-extrabold text-xl" style={{ color: meta.color }}>
                      #{idx + 1}
                    </span>
                    <Icon className="h-4 w-4" style={{ color: meta.color }} />
                    <span className="font-semibold text-slate-100 text-sm">{item.lot.name}</span>
                  </div>
                </div>
                <div className="text-[11px] space-y-1 font-mono-tab">
                  <div className="flex justify-between text-slate-400">
                    <span>Ricavi/m²</span><span className="text-emerald-300">{fmtEUR(item.calc.revenue_per_m2)}</span>
                  </div>
                  <div className="flex justify-between text-slate-400">
                    <span>EBITDA/m²</span><span className="text-sky-300">{fmtEUR(item.calc.ebitda_per_m2)}</span>
                  </div>
                  <div className="flex justify-between text-slate-400">
                    <span>EBITDA/CAPEX</span><span className="text-violet-300">{fmtPct((item.calc.ebitda_over_capex || 0) * 100, 1)}</span>
                  </div>
                  <div className="flex justify-between text-slate-400">
                    <span>Payback</span><span className="text-amber-300">{item.calc.payback_cashflow ? `${item.calc.payback_cashflow.toFixed(1)}y` : "N/D"}</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        <div className="mt-3 text-[11px] text-slate-500 italic">
          <Info className="h-3 w-3 inline mr-1" />
          Recommendation based on current assumptions. Il ranking non modifica gli input.
        </div>
      </Card>

      {/* TO_VALIDATE items */}
      <Card className="bg-slate-900/70 border-rose-500/20 border p-5">
        <div className="flex items-center gap-3 mb-3">
          <AlertTriangle className="h-5 w-5 text-rose-400" />
          <h3 className="font-display text-lg font-semibold text-slate-100">Parametri TO_VALIDATE</h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-xs">
          <ValidateItem label="Distribuzione specie Lotto 3 (Aronia/Ribes/Mirtillo/Olivello/Mora/Lampone)" />
          <ValidateItem label="Distribuzione specie Lotto 4 (6 funghi funzionali)" />
          <ValidateItem label="Cultivar italiano Luppolo Lotto 5" />
          <ValidateItem label="Concentrazioni principio attivo per estrazione" />
          <ValidateItem label="Rese di trasformazione fresco→essiccato→estratto" />
          <ValidateItem label="Prezzi B2B ingredienti per settore (nutraceutica/cosmetica/functional food)" />
          <ValidateItem label="CAPEX estrattori · essiccatori · rack per ogni lotto" />
          <ValidateItem label="OPEX personale, energia, substrati, packaging" />
          <ValidateItem label="Consumi energia specifici (kWh/kg biomassa · kWh/kg prodotto)" />
          <ValidateItem label="Consumi acqua specifici (m³/kg biomassa · % ricircolo)" />
          <ValidateItem label="Alfa-acidi, beta-acidi, xantumolo per cultivar Lotto 5" />
          <ValidateItem label="Beta-glucani, triterpeni, ergosterolo per specie fungine Lotto 4" />
        </div>
      </Card>
    </div>
  );
};

const SummaryPill = ({ label, value, accent }) => (
  <div className="border border-slate-800 rounded-md p-2.5 bg-slate-950/50">
    <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">{label}</div>
    <div className={`mt-0.5 font-display font-extrabold text-lg font-mono-tab ${accent}`}>{value}</div>
  </div>
);

const MiniStat = ({ label, value, tag, testId }) => (
  <div data-testid={testId} className="border border-slate-800 rounded-md p-2 bg-slate-950/40">
    <div className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider text-slate-500">
      <Ruler className="h-3 w-3" />
      <span className="truncate">{label}</span>
    </div>
    <div className="mt-1 flex items-center justify-between gap-2">
      <span className="font-mono-tab text-slate-100 font-semibold">{value}</span>
      <Tag value={tag} />
    </div>
  </div>
);

const KpiCell = ({ label, value, tag, accent, testId }) => (
  <div data-testid={testId} className="border border-slate-800 rounded-md p-2.5 bg-slate-950/50">
    <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 flex items-center justify-between gap-1">
      <span className="truncate">{label}</span>
      <Tag value={tag} />
    </div>
    <div className={`mt-1 font-mono-tab font-bold text-base ${accent}`}>{value}</div>
  </div>
);

const ValidateItem = ({ label }) => (
  <div className="flex items-start gap-2 p-1.5 border border-slate-800/60 rounded-md bg-slate-950/30">
    <input type="checkbox" disabled className="mt-1 accent-rose-500" />
    <span className="text-slate-300 text-[11px]">{label}</span>
  </div>
);
